// General
import "./live-introduction-dialog.scss";
import { useState, useEffect, forwardRef } from "react";
// Redux
import { useSelector, useDispatch } from "react-redux";
import { updateLiveIntroductionDialog } from "../../../../redux/store/dialogStore";
// react-gtm-module
import TagManager from "react-gtm-module";
// Material UI
import { Dialog, Slide } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
// i18next
import { useTranslation, Trans } from "react-i18next";
// Custom Hooks
import AssetManager from "../../../utility/manager/asset-manager/asset-manager";

const LiveIntroductionDialog = () => {
  // General variables
  const [page, setPage] = useState(0);

  // Render variables
  let pageView;
  let titleView;
  let descriptionView;
  let backgroundImageView;

  // Redux variables
  const liveIntroductionDialog = useSelector(
    (state) => state.dialog.liveIntroductionDialog
  );
  const dispatch = useDispatch();

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const getAsset = AssetManager();

  // Lifecycle | Mounted
  useEffect(() => {
    if (!liveIntroductionDialog) return;

    TagManager.dataLayer({
      dataLayer: {
        event: "PWA-LiveIntroduction-Dialog",
      },
    });
  }, [liveIntroductionDialog]);

  // Event Handlers | Button
  const onPrevious = () => {
    setPage((prevPage) => {
      if (prevPage === 0) {
        return 0;
      }
      return prevPage - 1;
    });
  };
  const onNext = () => {
    setPage((prevPage) => {
      if (prevPage === 4) {
        return 4;
      }
      return prevPage + 1;
    });
  };

  // Event Handlers | MUI Dialog
  const onCloseDialog = () => {
    dispatch(updateLiveIntroductionDialog(false));
  };

  // Render Functions
  switch (page) {
    case 0:
      pageView = <div className="page-container"></div>;
      titleView = (
        <div className="live-title">
          <Trans i18nKey="onboarding.live_title" components={{ br: <br /> }} />
        </div>
      );
      descriptionView = (
        <div className="live-description">
          <Trans i18nKey="onboarding.live_desc" components={{ br: <br /> }} />
        </div>
      );
      backgroundImageView = (
        <img
          className="live-background"
          src={getAsset("onboardingLive1Background")}
        />
      );
      break;
    case 1:
      pageView = <div className="page-container">{page}/4</div>;
      titleView = (
        <div className="live-title">
          <Trans i18nKey="onboarding.leaderboard_title" />
        </div>
      );
      descriptionView = (
        <div className="live-description">
          <Trans i18nKey="onboarding.leaderboard_desc" />
        </div>
      );
      backgroundImageView = (
        <img
          className="live-background"
          src={getAsset("onboardingLive2Background")}
        />
      );
      break;
    case 2:
      pageView = <div className="page-container">{page}/4</div>;
      titleView = (
        <div className="live-title">
          <Trans i18nKey="onboarding.interaction_title" />
        </div>
      );
      descriptionView = (
        <div className="live-description">
          <Trans i18nKey="onboarding.interaction_desc" />
        </div>
      );
      backgroundImageView = (
        <img
          className="live-background"
          src={getAsset("onboardingLive3Background")}
        />
      );
      break;
    case 3:
      pageView = <div className="page-container">{page}/4</div>;
      titleView = (
        <div className="live-title">
          <Trans i18nKey="onboarding.gift_title" />
        </div>
      );
      descriptionView = (
        <div className="live-description">
          <Trans i18nKey="onboarding.gift_desc" />
        </div>
      );
      backgroundImageView = (
        <img
          className="live-background"
          src={getAsset("onboardingLive4Background")}
        />
      );
      break;
    case 4:
      pageView = <div className="page-container">{page}/4</div>;
      titleView = (
        <div className="live-title">
          <Trans i18nKey="onboarding.coin_title" />
        </div>
      );
      descriptionView = (
        <div className="live-description">
          <Trans i18nKey="onboarding.coin_desc" />
        </div>
      );
      backgroundImageView = (
        <img
          className="live-background"
          src={getAsset("onboardingLive5Background")}
        />
      );
      break;
    default:
      break;
  }

  return (
    <Dialog
      fullScreen
      open={liveIntroductionDialog}
      TransitionComponent={Transition}
      onClose={onCloseDialog}
    >
      <div id="live-introduction-dialog">
        <div className="header">
          {page === 0 && (
            <div className="close-button-container">
              <CloseIcon className="close-button" onClick={onCloseDialog} />
            </div>
          )}
        </div>

        <div className="padding-container">
          {pageView}

          {titleView}

          {descriptionView}

          <div className="live-background-container">
            <div className="arrow-back-icon-container" onClick={onPrevious}>
              {page !== 0 && <ArrowBackIosIcon />}
            </div>
            {backgroundImageView}

            <div className="arrow-forward-icon-container" onClick={onNext}>
              {page !== 4 && <ArrowForwardIosIcon />}
            </div>
          </div>
        </div>

        {page === 4 && (
          <div className="footer">
            <div className="done-button" onClick={onCloseDialog}>
              {t("common.done")}
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default LiveIntroductionDialog;

// Render Functions | MUI Dialog | Will not work if inside of the same render as the Dialog
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});
